import { makeStyles } from "@material-ui/core/styles";
import { rv } from "../../tokens";
import { AlertCircleIcon } from "../icons";

export type PillValue = string | number | boolean;

export interface PillOption {
  label: string;
  value: PillValue;
}

const useStyles = makeStyles((theme) => ({
  root: { marginBottom: theme.spacing(0.5) },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    color: rv.muted,
    marginBottom: 7,
  },
  pills: { display: "flex", flexWrap: "wrap", gap: 8 },
  rowWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${rv.hairline3}`,
  },
  rowLabel: { fontSize: 13.5, color: rv.body, flex: 1 },
  pill: {
    appearance: "none",
    cursor: "pointer",
    border: `1.5px solid ${rv.inputStroke}`,
    background: "#fff",
    color: "#5A5A5A",
    borderRadius: rv.radiusPill,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    minHeight: 40,
    font: "inherit",
    transition: "all .15s ease",
  },
  pillSelected: {
    background: rv.gold,
    color: rv.onDark,
    borderColor: rv.gold,
  },
  error: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    color: rv.errorFg,
    fontSize: 11.5,
    fontWeight: 500,
    marginTop: 5,
  },
}));

interface Props {
  label?: string;
  value: PillValue | "" | undefined;
  onChange: (value: PillValue) => void;
  options: PillOption[];
  error?: string;
  required?: boolean;
  row?: boolean;
}

export default function SegmentedPills({
  label,
  value,
  onChange,
  options,
  error,
  required,
  row,
}: Props) {
  const classes = useStyles();

  const pills = (
    <div className={classes.pills}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            type="button"
            key={String(option.value)}
            className={`${classes.pill} ${selected ? classes.pillSelected : ""}`}
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  if (row) {
    return (
      <div>
        <div className={classes.rowWrap}>
          <span className={classes.rowLabel}>
            {label}
            {required && <span style={{ color: rv.errorFg }}> *</span>}
          </span>
          {pills}
        </div>
        {error && (
          <span className={classes.error} data-rv-error="true">
            <AlertCircleIcon size={13} />
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {label && (
        <label className={classes.label}>
          {label}
          {required && <span style={{ color: rv.errorFg }}> *</span>}
        </label>
      )}
      {pills}
      {error && (
        <span className={classes.error} data-rv-error="true">
          <AlertCircleIcon size={13} />
          {error}
        </span>
      )}
    </div>
  );
}
